<script setup lang="ts">
import { computed, ref } from "vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { practiceLongQuestions, practiceUnits } from "~/features/computer/data/practice/practice.data";

const selectedUnit = ref<(typeof practiceUnits)[number]["value"]>("all");
const openAnswer = ref<string | null>(null);

const filteredQuestions = computed(() => practiceLongQuestions.filter((question) => (
  selectedUnit.value === "all" || question.unit === selectedUnit.value
)));

const faqs = [
  {
    question: "Practice long questions page kis liye useful hai?",
    answer:
      "Yeh page long-answer revision aur self-check ke liye useful hai. Student answer khol kar structure, steps aur explanation ko compare kar sakta hai.",
  },
  {
    question: "Kya practice long questions me chapter filter hota hai?",
    answer:
      "Haan, is page par unit filter hota hai jisse aap Python, Algorithms ya dusre chapters ke long questions alag se revise kar sakte hain.",
  },
  {
    question: "Practice long questions aur long questions hub me kya difference hai?",
    answer:
      "Long questions hub chapter-wise entry point hai, jab ke practice long questions page mixed revision aur direct answer review ke liye zyada useful hota hai.",
  },
];

function toggleAnswer(id: string) {
  openAnswer.value = openAnswer.value === id ? null : id;
}

usePageSeo({
  title: "Practice Long Questions | 11th Class Computer",
  description: "11th class computer ke long questions ko unit filters aur structured answers ke sath exam prep mode me kholo.",
  keywords: "11th class computer long questions practice, first year computer long questions revision, computer descriptive practice",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Practice Mode", item: "/11th/computer/practice/" },
    { name: "Long Questions", item: "/11th/computer/practice/long-questions/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Practice Long Questions",
    items: [
      { name: "Long Questions Hub", path: "/11th/computer/long-questions/" },
      { name: "Guess Paper", path: "/11th/computer/guess-paper/" },
      { name: "Practice MCQs", path: "/11th/computer/practice/mcqs/" },
      { name: "Practice Short Questions", path: "/11th/computer/practice/short-questions/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="space-y-4 rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">Long Questions (Exam Prep)</h1>
        <p class="text-sm leading-7 text-slate-200">Structured answers ke sath long questions revise karo aur weak unit par wapas jao.</p>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5">
        <h2 class="text-lg font-semibold text-slate-900">Units</h2>
        <div class="mt-4 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none]">
          <button
            v-for="unit in practiceUnits"
            :key="unit.value"
            type="button"
            class="snap-start shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition"
            :class="selectedUnit === unit.value ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'"
            @click="selectedUnit = unit.value"
          >
            {{ unit.label }}
          </button>
        </div>
      </section>

      <section class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
        <div v-for="question in filteredQuestions" :key="question.id" class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div class="flex gap-2 text-xs font-medium">
            <span class="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{{ question.unitLabel }}</span>
            <span class="rounded-full bg-sky-100 px-3 py-1 text-sky-800">{{ question.topicLabel }}</span>
          </div>
          <p class="mt-3 text-sm font-semibold text-slate-900">{{ question.question }}</p>
          <button type="button" class="mt-3 rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-800" @click="toggleAnswer(question.id)">
            {{ openAnswer === question.id ? "Jawab chhupao" : "Jawab dekho" }}
          </button>
          <div v-if="openAnswer === question.id" class="mt-4 space-y-3">
            <ol v-if="question.steps?.length" class="space-y-2 text-sm text-slate-700">
              <li v-for="(step, index) in question.steps" :key="`${question.id}-${index}`">{{ index + 1 }}. {{ step }}</li>
            </ol>
            <p class="text-sm leading-7 text-slate-700">{{ question.solution }}</p>
            <pre v-if="question.code" class="overflow-x-auto rounded-2xl bg-slate-950 p-4 text-xs text-slate-100"><code>{{ question.code }}</code></pre>
          </div>
          <div class="mt-3 flex flex-wrap gap-3 text-sm">
            <NuxtLink :to="question.conceptTo" class="font-medium text-sky-700 underline decoration-sky-300 underline-offset-4">Yeh topic dobara parho -></NuxtLink>
            <NuxtLink :to="question.chapterTo" class="font-medium text-slate-700 underline decoration-slate-300 underline-offset-4">Chapter page kholo -></NuxtLink>
          </div>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Practice Long Questions FAQs</h2>
        </div>
        <div class="mt-5 space-y-4">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>

